import React from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Autocomplete, Modal, Stack, TextField, Typography } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useMaterialUIController } from "context";

const parametersTypeOptions = [
  {
    label: "Increase",
    value: "Increase",
  },
  {
    label: "Decrease",
    value: "Decrease",
  },
];

const CustomOptionModal = ({ open, handleClose, data, handleSubmit }) => {
  const [controller] = useMaterialUIController();

  const { darkMode } = controller;

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: window.innerWidth < 400 ? "100%" : 400,
    bgcolor: darkMode ? "#1F283E" : "background.paper",
    boxShadow: 24,
    p: 4,
    outline: "none",
    borderRadius: "0.5rem",
  };

  const validationSchema = Yup.object(
    data.for === "parameters"
      ? {
          type: Yup.object().shape({
            label: Yup.string().required("Label is required"),
            value: Yup.string()
              .oneOf(
                parametersTypeOptions.map((option) => option.value),
                "Invalid type value"
              )
              .required("Type value is required"),
          }),
          value: Yup.number()
            .min(0, "Value must be between 0 and 100")
            .max(100, "Value must be between 0 and 100")
            .required("Value is required"),
        }
      : {
          value: Yup.number().required("Field is required"),
        }
  );

  const handleOnSubmit = (formData) => {
    handleSubmit({ ...data, ...formData });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={style}>
        <Typography variant="h4" mb={2}>
          Add Option for {data.label}
        </Typography>
        <Formik
          enableReinitialize
          validateOnChange
          validationSchema={validationSchema}
          initialValues={
            data.for === "parameters"
              ? {
                  type: parametersTypeOptions[0],
                  value: data?.value,
                }
              : {
                  value: data?.value,
                }
          }
          onSubmit={handleOnSubmit}
        >
          {({ values, setFieldValue, touched, errors }) => (
            <Form
              onKeyDown={(keyEvent) => {
                if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
                  keyEvent.preventDefault();
                }
              }}
            >
              {data.for === "parameters" ? (
                <Stack
                  spacing={2}
                  sx={{
                    "& .MuiFormHelperText-root": { color: "#d32f2f" },
                    "& .MuiInputLabel-formControl": { lineHeight: "20px !important" },
                  }}
                >
                  <Autocomplete
                    fullWidth
                    id="type"
                    disableClearable
                    options={parametersTypeOptions}
                    value={values.type}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    onChange={(_, value) => {
                      setFieldValue("type", value || null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Type"
                        error={touched.type && Boolean(errors.type)}
                        helperText={touched.type && errors.type}
                      />
                    )}
                  />
                  <MDInput
                    name="value"
                    label="Percentage"
                    type="number"
                    value={values?.value}
                    onChange={(event) => {
                      setFieldValue("value", event.target.value);
                    }}
                    helperText={touched.value && errors.value}
                    error={touched.value && Boolean(errors.value)}
                    fullWidth
                  />
                </Stack>
              ) : (
                <Stack
                  spacing={2}
                  sx={{
                    "& .MuiFormHelperText-root": { color: "#d32f2f" },
                    "& .MuiInputLabel-formControl": { lineHeight: "20px !important" },
                  }}
                >
                  <MDInput
                    name="value"
                    label="Trailing Average"
                    type="number"
                    value={values?.value}
                    onChange={(event) => {
                      setFieldValue("value", event.target.value);
                    }}
                    helperText={touched.value && errors.value}
                    error={touched.value && Boolean(errors.value)}
                    fullWidth
                  />
                </Stack>
              )}

              <MDBox sx={{ display: "flex", justifyContent: "end" }}>
                <MDButton
                  color="gray80"
                  size="small"
                  onClick={handleClose}
                  sx={{ mt: 2, mr: 1, position: "relative" }}
                >
                  Cancel
                </MDButton>
                <MDButton
                  color="primaryRed"
                  size="small"
                  type="submit"
                  sx={{ mt: 2, position: "relative" }}
                >
                  Add
                </MDButton>
              </MDBox>
            </Form>
          )}
        </Formik>
      </MDBox>
    </Modal>
  );
};

export default CustomOptionModal;
